import { Badge, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@commenda/kakashi';
import type { SharesComputation } from './compute';
import { type OwnershipEntry, ownerDisplayName } from './types';

interface SharesSummaryProps {
  ids: string[];
  entries: Record<string, OwnershipEntry>;
  computation: SharesComputation;
  // ISO-4217 code shown in the capital column header.
  currency: string;
}

const DASH = '—';
// Slight left indent for data + subtotal rows; group headers and the Total row sit flush.
const INDENT = 'pl-4';
const fmtShares = (n: number) => n.toLocaleString('en-US');
const fmtPercent = (n: number) => `${n.toFixed(2)}%`;
const fmtMoney = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const GroupLabel = ({ children }: { children: React.ReactNode }) => (
  <TableRow className="hover:bg-transparent">
    <TableCell colSpan={4} className="txt-xs-medium pt-5 uppercase tracking-wide text-text-tertiary">
      {children}
    </TableCell>
  </TableRow>
);

export const SharesSummary = ({ ids, entries, computation, currency }: SharesSummaryProps) => {
  const { perOwnerShares, perOwnerCapital, issuedOutstanding, esopPoolShares, unissuedReserve, authorizedShares, totalCapital, percentTotal } =
    computation;
  const reservedSubtotal = esopPoolShares + unissuedReserve;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h3 className="text-base font-bold text-text-primary">Ownership Summary</h3>
        <p className="text-sm text-text-secondary">
          The equity pool shares aren&apos;t reflected in this summary. Pool shares remain unissued until options are
          granted and exercised by their holders. You&apos;ll need to set up an employee stock option plan separately
          before granting options.
        </p>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Shareholder</TableHead>
            <TableHead className="text-right">Shares</TableHead>
            <TableHead className="text-right">% owned</TableHead>
            <TableHead className="text-right">Capital contribution ({currency})</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <GroupLabel>Issued &amp; Outstanding</GroupLabel>
          {ids.map((id, i) => {
            const entry = entries[id];
            if (!entry) return null;
            return (
              <TableRow key={id}>
                <TableCell className={INDENT}>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{ownerDisplayName(entry.owner)}</span>
                    <Badge variant="gray" size="sm">
                      {entry.owner.kind === 'individual' ? 'Individual' : 'Entity'}
                    </Badge>
                  </div>
                </TableCell>
                <TableCell className="text-right">{fmtShares(perOwnerShares[i] ?? 0)}</TableCell>
                <TableCell className="text-right">{fmtPercent(entry.ownershipPercent ?? 0)}</TableCell>
                <TableCell className="text-right">{fmtMoney(perOwnerCapital[i] ?? 0)}</TableCell>
              </TableRow>
            );
          })}
          <TableRow className="txt-small-bold">
            <TableCell className={INDENT}>Subtotal</TableCell>
            <TableCell className="text-right">{fmtShares(issuedOutstanding)}</TableCell>
            <TableCell className="text-right">{fmtPercent(percentTotal)}</TableCell>
            <TableCell className="text-right">{fmtMoney(totalCapital)}</TableCell>
          </TableRow>

          {/* Reserved & Unissued — hidden entirely when nothing is reserved
              (all authorized shares issued, no ESOP). The Unissued row itself
              only shows when there are unissued shares. */}
          {reservedSubtotal > 0 ? (
            <>
              <GroupLabel>Reserved &amp; Unissued</GroupLabel>
              {esopPoolShares > 0 ? (
                <TableRow>
                  <TableCell className={INDENT}>
                    <div className="flex items-center gap-2">
                      <span className="text-sm">ESOP Pool</span>
                      <Badge variant="gray" size="sm">
                        Reserved
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">{fmtShares(esopPoolShares)}</TableCell>
                  <TableCell className="text-right">{DASH}</TableCell>
                  <TableCell className="text-right">{DASH}</TableCell>
                </TableRow>
              ) : null}
              {unissuedReserve > 0 ? (
                <TableRow>
                  <TableCell className={INDENT}>Unissued reserve</TableCell>
                  <TableCell className="text-right">{fmtShares(unissuedReserve)}</TableCell>
                  <TableCell className="text-right">{DASH}</TableCell>
                  <TableCell className="text-right">{DASH}</TableCell>
                </TableRow>
              ) : null}
              <TableRow className="txt-small-bold">
                <TableCell className={INDENT}>Subtotal</TableCell>
                <TableCell className="text-right">{fmtShares(reservedSubtotal)}</TableCell>
                <TableCell className="text-right">{DASH}</TableCell>
                <TableCell className="text-right">{DASH}</TableCell>
              </TableRow>
            </>
          ) : null}

          <TableRow className="bg-bg-secondary text-sm font-bold">
            <TableCell>Total</TableCell>
            <TableCell className="text-right">{fmtShares(authorizedShares)}</TableCell>
            <TableCell className="text-right">{fmtPercent(percentTotal)}</TableCell>
            <TableCell className="text-right">{fmtMoney(totalCapital)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
};
